import { useCallback } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { serverErrorMessage } from "../lib/apiError";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { useCachedQuery } from "./useCachedQuery";

// Mirrors AdminSecureNoteView on the backend.
export type AdminSecureNote = {
  id: string;
  ownerEmail: string;
  name: string;
  createdAt: string;
  expiresAt: string;
};

// Mirrors RevealedSecureNoteView. Only ever returned by the audited
// POST /reveal — never cached, never persisted.
export type RevealedSecureNote = AdminSecureNote & { value: string };

export type AdminSecureNotesStatus = "loading" | "ready" | "error";

export type AdminSecureNotesResult<T> =
  | { ok: true; value: T }
  | { ok: false; message: string };

export type UseAdminSecureNotesResult = {
  status: AdminSecureNotesStatus;
  notes: ReadonlyArray<AdminSecureNote>;
  errorMessage: string | null;
  refetch: () => Promise<void>;
  reveal: (id: string) => Promise<AdminSecureNotesResult<RevealedSecureNote>>;
  deleteNote: (id: string) => Promise<AdminSecureNotesResult<void>>;
};

const LIST_URL = ADMIN_QUERIES.secureNotes.url;

const fetchNotes = async (signal: AbortSignal): Promise<ReadonlyArray<AdminSecureNote>> => {
  const res = await fetch(LIST_URL, { credentials: "include", signal });
  if (!res.ok) throw new Error(`Failed to load secure notes (${res.status})`);
  return (await res.json()) as ReadonlyArray<AdminSecureNote>;
};

export const useAdminSecureNotes = (): UseAdminSecureNotesResult => {
  const { status, data, error, refetch, mutate } = useCachedQuery<ReadonlyArray<AdminSecureNote>>({
    cacheResource: ADMIN_QUERIES.secureNotes.resource,
    queryKey: LIST_URL,
    fetcher: fetchNotes,
  });
  const notes = data ?? [];

  const reveal = useCallback(
    async (id: string): Promise<AdminSecureNotesResult<RevealedSecureNote>> => {
      try {
        const res = await fetch(`${LIST_URL}/${encodeURIComponent(id)}/reveal`, {
          method: "POST",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (res.status === 200) {
          return { ok: true, value: (await res.json()) as RevealedSecureNote };
        }
        return { ok: false, message: await serverErrorMessage(res, "Reveal failed") };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [],
  );

  const deleteNote = useCallback(
    async (id: string): Promise<AdminSecureNotesResult<void>> => {
      try {
        const res = await fetch(`${LIST_URL}/${encodeURIComponent(id)}`, {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (res.status === 204) {
          // Write-through (useAdminClaws pattern): no follow-up GET whose
          // failure would flip the whole table to the error state.
          mutate((prev) => prev.filter((n) => n.id !== id));
          return { ok: true, value: undefined };
        }
        return { ok: false, message: await serverErrorMessage(res, "Delete failed") };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [mutate],
  );

  return { status, notes, errorMessage: error, refetch, reveal, deleteNote };
};
